import React, { useState } from 'react';
import { Input, Button, Modal, Spin } from 'antd';
import { useCasualPayrollForm } from './hooks/useCasualPayrollForm';

// Number input that won't lose focus during typing
const NumInput = ({ value, onChange, disabled, width = 130 }: {
  value: number; onChange: (v: number) => void; disabled: boolean; width?: number;
}) => {
  const [text, setText] = useState(String(value));
  // Sync from parent when value changes externally (e.g. record navigation)
  React.useEffect(() => { setText(String(value)); }, [value]);
  return (
    <Input
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => { const n = parseInt(text, 10); onChange(isNaN(n) ? 0 : n); }}
      disabled={disabled}
      style={{ width, textAlign: 'right' }}
    />
  );
};

// Field must be defined OUTSIDE the component to avoid re-mount on every render
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>
);

const P913DForm: React.FC = () => {
  const f = useCasualPayrollForm();
  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!f.current) return;
    Modal.confirm({
      title: '確認刪除', content: `確定要刪除「${f.current.f93d_01} ${f.current.f93d_11}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: f.doDelete,
    });
  };

  const modeLabel = f.mode === 'add' ? '新增' : f.mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={f.loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          非正式人員印領資 (pw913df1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {f.records.length > 0 ? `${f.currentIdx + 1} / ${f.records.length}` : '無資料'}
          </span>
        </h2>

        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入代號查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && f.quickSearch(searchText.trim())}
            disabled={f.isEditing} style={{ width: 200 }} />
          <Button onClick={() => searchText.trim() && f.quickSearch(searchText.trim())} disabled={f.isEditing}>查詢</Button>
          <span className="mx-2" />
          <Button size="small" onClick={f.navFirst} disabled={f.isEditing || f.records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={f.navPrev} disabled={f.isEditing || f.currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={f.navNext} disabled={f.isEditing || f.currentIdx >= f.records.length - 1}>&gt;</Button>
          <Button size="small" onClick={f.navLast} disabled={f.isEditing || f.records.length === 0}>&gt;|</Button>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex gap-4">
            <Field label="員工代號 (F93D_01)">
              <Input value={f.code} onChange={e => f.setCode(e.target.value)} maxLength={8}
                disabled={f.mode !== 'add'} style={{ width: 150 }} />
            </Field>
            <Field label="員工姓名 (F93D_11)">
              <Input value={f.name} onChange={e => f.setName(e.target.value)} maxLength={10}
                disabled={!f.isEditing} style={{ width: 150 }} />
            </Field>
            <Field label="起始年月 (F93D_12)">
              <Input value={f.startYM} onChange={e => f.setStartYM(e.target.value)} maxLength={6}
                disabled={!f.isEditing} style={{ width: 120 }} placeholder="YYYYMM" />
            </Field>
            <Field label="結束年月 (F93D_13)">
              <Input value={f.endYM} onChange={e => f.setEndYM(e.target.value)} maxLength={6}
                disabled={!f.isEditing} style={{ width: 120 }} placeholder="YYYYMM" />
            </Field>
          </div>

          <div className="flex gap-4 flex-wrap">
            <Field label="本薪">
              <NumInput value={f.salary} onChange={f.setSalary} disabled={!f.isEditing} />
            </Field>
            <Field label="伙食津貼">
              <NumInput value={f.meal} onChange={f.setMeal} disabled={!f.isEditing} />
            </Field>
            <Field label="加班費">
              <NumInput value={f.overtime} onChange={f.setOvertime} disabled={!f.isEditing} />
            </Field>
          </div>

          <div className="flex gap-4 flex-wrap">
            <Field label="代扣稅額">
              <NumInput value={f.tax} onChange={f.setTax} disabled={!f.isEditing} />
            </Field>
            <Field label="勞保費">
              <NumInput value={f.labor} onChange={f.setLabor} disabled={!f.isEditing} />
            </Field>
            <Field label="健保費">
              <NumInput value={f.health} onChange={f.setHealth} disabled={!f.isEditing} />
            </Field>
            <Field label="退休金提繳">
              <NumInput value={f.pension} onChange={f.setPension} disabled={!f.isEditing} />
            </Field>
          </div>
        </div>

        {f.current && f.mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4">
            建立: {f.current.f93d_91} | 日期: {f.current.f93d_92 || '-'} | 修改: {f.current.f93d_93}
          </div>
        )}

        <div className="flex items-center gap-2">
          {!f.isEditing ? (
            <>
              <Button type="primary" onClick={f.startAdd}>新增</Button>
              <Button onClick={f.startEdit} disabled={!f.current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!f.current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={f.save}>儲存</Button>
              <Button onClick={f.cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P913DForm;
